import { createRoot } from "react-dom/client";
import App from "./App.tsx";

// Self-hosted fonts (latin subset only) — removes render-blocking third-party Google Fonts request
import "@fontsource/cormorant-garamond/latin-400.css";
import "@fontsource/cormorant-garamond/latin-500.css";
import "@fontsource/cormorant-garamond/latin-600.css";
import "@fontsource/cormorant-garamond/latin-700.css";
import "@fontsource/lato/latin-300.css";
import "@fontsource/lato/latin-400.css";
import "@fontsource/lato/latin-400-italic.css";
import "@fontsource/lato/latin-700.css";
import "@fontsource/cinzel/latin-400.css";
import "@fontsource/cinzel/latin-500.css";
import "@fontsource/cinzel/latin-600.css";
import "@fontsource/cinzel/latin-700.css";
import "@fontsource/allura/latin-400.css";

import "./index.css";

createRoot(document.getElementById("root")!).render(<App />);

// Lazy-load PostHog only after the user interacts (or after a long idle fallback),
// keeping the ~180KB analytics bundle off the main thread during initial load.
if (typeof window !== "undefined") {
  let started = false;
  const events = ["pointerdown", "keydown", "scroll", "touchstart"] as const;

  const start = () => {
    if (started) return;
    started = true;
    events.forEach((e) => window.removeEventListener(e, start));
    import("posthog-js").then(({ default: posthog }) => {
      import("./lib/posthog").then(({ POSTHOG_KEY, posthogOptions }) => {
        posthog.init(POSTHOG_KEY, posthogOptions);
      });
    });
  };

  events.forEach((e) => window.addEventListener(e, start, { once: true, passive: true }));

  // Fallback for users who never interact. Arm this only AFTER `load` — an idle
  // callback registered here fires in the first main-thread gap, which lands
  // mid-paint and puts the 60 KB analytics chunk on the critical request chain,
  // competing with the LCP image and fonts. Waiting for `load` keeps analytics
  // out of the initial-load window entirely.
  // `load` can fire before a lazy route's own chunk has rendered its content (on /events it fired
  // first), so the fallback also waits 3 s past load. That keeps PostHog and its session recorder,
  // about 130 KB together, out of the window where the page is still painting.
  const scheduleIdleStart = () => {
    window.setTimeout(() => {
      if (window.requestIdleCallback) {
        window.requestIdleCallback(start, { timeout: 6000 });
      } else {
        start();
      }
    }, 3000);
  };

  if (document.readyState === "complete") {
    scheduleIdleStart();
  } else {
    window.addEventListener("load", scheduleIdleStart, { once: true });
  }
}
